import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import * as i18n from "util/i18n";

import { selectors as investmentfundsSelectors } from "reducers/investmentfunds";
import Container from "pages/_components/Container";
import CustomText from "pages/_components/CustomText/CustomText";
import "../../../styles/components/modules/_DashboardCard.scss";

const EPS = 0.005; // umbral para evitar falsos ceros por flotantes

const Bac = ({ tenencia, currency, dollarRate }) => {
    const accounts = Array.isArray(tenencia) ? tenencia : [];

    // Helper: últimos 5 dígitos sin ceros a la izquierda
    const last5 = (n = "") => {
        const raw = String(n || "").replace(/^0+/, "");
        const chunk = raw.slice(-5) || raw;
        return chunk.padStart(5, "•");
    };

    const { perAccount, grandTotal, isEmpty } = useMemo(() => {
        // Cambio de moneda sin ternarios anidados
        let fx = 1;
        if (currency === "USD") {
            fx = dollarRate > 0 ? dollarRate : 1;
        }

        const perAcc = (accounts || []).map((acc) => {
            const acctNumber = acc?.accountId?.number ?? acc?.accountNumber ?? "";
            const lista = Array.isArray(acc?.listaPorEspecie) ? acc.listaPorEspecie : [];

            const items = lista
                .map((item) => {
                    const enPesos = Number(item?.saldo?.enPesos) || 0;
                    const monto = currency === "ARS" ? enPesos : enPesos / fx;
                    return {
                        codigo: item?.especie?.codigo || "",
                        descripcion: item?.especie?.descripcion || "",
                        monto,
                    };
                })
                .filter((x) => (x?.monto || 0) > EPS); // solo especies con valor

            const total = items.reduce((s, x) => s + (x.monto || 0), 0);

            return {
                accountNumber: acctNumber,
                label: `Cuenta Comitente (${last5(acctNumber)})`,
                items, // lista completa para total y slice
                total,
            };
        });

        // Filtrar cuentas sin valor
        const perAccNonEmpty = perAcc.filter((a) => (a.total || 0) > EPS);

        // Total global
        const grand = perAccNonEmpty.reduce((s, a) => s + (a.total || 0), 0);

        return { perAccount: perAccNonEmpty, grandTotal: grand, isEmpty: grand <= EPS };
    }, [accounts, currency, dollarRate]);

    const format = (value) =>
        Number(value || 0).toLocaleString("es-AR", {
            style: "currency",
            currency: currency === "ARS" ? "ARS" : "USD",
            minimumFractionDigits: 2,
        });

    return (
        <div className="above-the-fold">
            <Container className="container--layout flex-grow">
                <div className={`card card--bac ${isEmpty ? "card--disabled" : ""}`}>
                    {/* Header */}
                    <div className="card__header">
                        <div>
                            <CustomText
                                text="investmentFunds.dashboard.BAC"
                                textType="I18N"
                                className="card__header-title"
                            />
                        </div>
                        {/* Si querés mostrar "Ver más", descomentá y agregá useDispatch + push */}
                        {/* {!isEmpty && (
              <Button
                bsStyle="link"
                className="card__header-action"
                onClick={() => dispatch(push("/investmentFunds/bac/list"))}
                disabled={isEmpty}
              >
                <CustomText text="global.showMore" textType="I18N" />
                <span className="card__header-icon">→</span>
              </Button>
            )} */}
                    </div>

                    {isEmpty ? (
                        <div className="card__empty-message">
                            <CustomText
                                text={i18n.get(
                                    "investmentFunds.dashboard.noInvest.BAC",
                                    "Actualmente, no posees Bonos, Acciones y CEDEARs",
                                )}
                                textType="plain"
                                className="card__empty-text"
                            />
                        </div>
                    ) : (
                        <>
                            {/* Columnas por cuenta */}
                            <div className="card__columns">
                                {perAccount.map((acc) => (
                                    <div className="card__column" key={acc.accountNumber || acc.label}>
                                        <div className="card__column-title">
                                            <CustomText text={acc.label} textType="plain" />
                                        </div>

                                        <ul className="card__list" aria-label={`BAC ${acc.label}`}>
                                            {acc.items.slice(0, 4).map(({ codigo, descripcion }) => (
                                                <li key={`${acc.accountNumber}-${codigo}`} className="card__list-item">
                                                    <span className="card__bullet card__bullet--bac" />
                                                    <CustomText text={descripcion || codigo} className="card__text" />
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>

                            {/* Total combinado (suma de TODOS los ítems con monto > 0) */}
                            <div className="card__total">
                                <CustomText
                                    text="investmentFunds.dashboard.BAC.totalAmount"
                                    textType="I18N"
                                    className="card__total-label"
                                />
                                <CustomText text={format(grandTotal)} textType="plain" className="card__total-value" />
                            </div>
                        </>
                    )}
                </div>
            </Container>
        </div>
    );
};

Bac.propTypes = {
    tenencia: PropTypes.arrayOf(
        PropTypes.shape({
            accountId: PropTypes.shape({
                number: PropTypes.string,
            }),
            accountNumber: PropTypes.string,
            listaPorEspecie: PropTypes.arrayOf(
                PropTypes.shape({
                    especie: PropTypes.shape({
                        codigo: PropTypes.string,
                        descripcion: PropTypes.string,
                    }),
                    saldo: PropTypes.shape({
                        enPesos: PropTypes.number,
                    }),
                }),
            ),
        }),
    ),
    currency: PropTypes.oneOf(["ARS", "USD"]).isRequired,
    dollarRate: PropTypes.number,
};

Bac.defaultProps = {
    tenencia: [],
    dollarRate: 0,
};

const mapStateToProps = (state) => ({
    tenencia: investmentfundsSelectors.getBac(state) || [],
});

export default connect(mapStateToProps)(Bac);
